import { ReactNode } from "react";
import dayjs from "dayjs";
import { cn } from "@/lib/utils";
import { SCHEDULE_CONSTANTS, JOB_BAR_COLOR, getDayBgClass } from "../utils";
import { useHolidays } from "@/hooks/use-holidays";
import { ViewMode } from "@/hooks/services/pps/handlers/schedule/job-schedule-types";
import ScheduleHoverCard, {
  type ScheduleHoverCardData
} from "./schedule-hover-card";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger
} from "@/components/ui/tooltip";

interface DailyGridLinesProps {
  columns?: number;
  colWidth?: number;
  /** The single day rendered in daily view — shades the whole strip grey/yellow when weekend/holiday. */
  day?: dayjs.ConfigType;
}

export const DailyGridLines = ({
  columns = SCHEDULE_CONSTANTS.TOTAL_COLUMNS,
  colWidth,
  day
}: DailyGridLinesProps) => {
  const holidays = useHolidays();
  // In daily view all 24 hour-columns belong to one date, so apply one background to all.
  const dayBg = day ? getDayBgClass(day, holidays) : "";
  return (
    <div className="aero:absolute aero:inset-0 aero:flex aero:z-0">
      {Array.from({ length: columns }).map((_, index) => (
        <div
          key={index}
          className={cn(
            "aero:flex-shrink-0 aero:border-r aero:border-neutral-300 aero:last:border-r-0",
            dayBg
          )}
          style={colWidth ? { width: colWidth } : { flex: 1 }}
        />
      ))}
    </div>
  );
};

interface SpanningGridLinesProps {
  days: Date[];
  viewMode: ViewMode;
  colWidth?: number;
}

// Borders only — the weekend/holiday/today tint is painted full-height behind all rows
// by DayColumnBackdrop (WOR-2187 R1), so this stays a thin per-row grid-line layer.
export const SpanningGridLines = ({
  days,
  colWidth
}: SpanningGridLinesProps) => (
  <div className="aero:absolute aero:inset-0 aero:flex aero:z-0 aero:pointer-events-none">
    {days.map((day) => (
      <div
        key={day.toISOString()}
        className="aero:flex-shrink-0 aero:border-r aero:border-neutral-300 aero:last:border-r-0"
        style={colWidth ? { width: colWidth } : { flex: 1 }}
      />
    ))}
  </div>
);

interface DayColumnBackdropProps {
  days: Date[];
  colWidth?: number;
}

/**
 * Full-height weekend-grey / holiday-yellow / today day-column background, mounted ONCE
 * behind all rows of a weekly grid so a holiday/weekend column is shaded top-to-bottom —
 * not just within row bands (WOR-2187 R1). Place at z-0 behind the (transparent) rows.
 */
export const DayColumnBackdrop = ({ days, colWidth }: DayColumnBackdropProps) => {
  const holidays = useHolidays();
  return (
    <div className="aero:absolute aero:inset-0 aero:flex aero:z-0 aero:pointer-events-none">
      {days.map((day) => (
        <div
          key={day.toISOString()}
          className={cn("aero:flex-shrink-0", getDayBgClass(day, holidays))}
          style={colWidth ? { width: colWidth } : { flex: 1 }}
        />
      ))}
    </div>
  );
};

interface LaneDividersProps {
  laneCount: number;
  laneHeight: number;
  className?: string;
}

export const LaneDividers = ({
  laneCount,
  laneHeight,
  className
}: LaneDividersProps) => {
  if (laneCount <= 1) return null;

  return (
    <>
      {Array.from({ length: laneCount - 1 }).map((_, index) => (
        <div
          key={`lane-divider-${index}`}
          className={cn(
            "aero:absolute aero:left-0 aero:right-0 aero:border-t aero:border-neutral-300 aero:z-[2]",
            className
          )}
          style={{ top: `${(index + 1) * laneHeight}px` }}
        />
      ))}
    </>
  );
};

interface ScheduleRowProps {
  rowKey: string | number;
  rowHeight: number;
  overviewContent: ReactNode;
  gridContent: ReactNode;
  className?: string;
}

export const ScheduleRow = ({
  rowKey,
  rowHeight,
  overviewContent,
  gridContent,
  className
}: ScheduleRowProps) => (
  <div
    key={rowKey}
    className={cn(
      "aero:flex aero:border-b aero:last:border-b-0 aero:hover:bg-muted/20",
      className
    )}
  >
    {overviewContent}
    <div
      className="aero:flex-1 aero:relative"
      style={{ minHeight: `${rowHeight}px` }}
    >
      {gridContent}
    </div>
  </div>
);

interface SpanningScheduleRowProps {
  rowKey: string | number;
  rowHeight: number;
  overviewContent: ReactNode;
  gridContent: ReactNode;
  gridWidth?: number;
  colWidth?: number;
  className?: string;
}

export const SpanningScheduleRow = ({
  rowKey,
  rowHeight,
  overviewContent,
  gridContent,
  gridWidth,
  className
}: SpanningScheduleRowProps) => (
  <div
    key={rowKey}
    className={cn(
      "aero:flex aero:border-b aero:last:border-b-0 aero:hover:bg-muted/20",
      className
    )}
  >
    {overviewContent}
    <div
      className="aero:relative"
      style={{
        minHeight: `${rowHeight}px`,
        width: gridWidth ? `${gridWidth}px` : undefined,
        flex: gridWidth ? undefined : 1
      }}
    >
      {gridContent}
    </div>
  </div>
);
interface ClippedIndicatorProps {
  position: "start" | "end";
}

export const ClippedIndicator = ({ position }: ClippedIndicatorProps) => (
  <span
    className={cn(
      "aero:absolute aero:top-0 aero:bottom-0 aero:w-1 aero:bg-white/30",
      position === "start" ? "aero:left-0" : "aero:right-0"
    )}
  />
);

export const getAssignmentBarClasses = (
  priorityColor: string,
  isStartClipped: boolean,
  isEndClipped: boolean,
  isDaily: boolean = true,
  textColorClass: string = "aero:text-white",
  borderVariant: "solid-left" | "dashed-full" = "solid-left"
) =>
  cn(
    "aero:absolute aero:z-[3] aero:overflow-hidden aero:cursor-pointer aero:transition-opacity aero:hover:opacity-90",
    textColorClass,
    borderVariant === "dashed-full"
      ? // Full dashed border + solid left accent via inner div; extra left padding clears the 3px accent
        "aero:border aero:border-dashed aero:rounded aero:pl-3 aero:pr-2 aero:pt-1 aero:pb-1 aero:text-xs"
      : [
          isDaily
            ? "aero:border-l-[3px] aero:pl-2 aero:pr-2 aero:pt-1 aero:pb-1 aero:text-xs"
            : "aero:border aero:px-2 aero:py-0.5 aero:text-[10px]",
          !isStartClipped && "aero:rounded-l",
          !isEndClipped && "aero:rounded-r"
        ],
    priorityColor
  );

export const calculateRowHeight = (
  laneCount: number,
  laneHeight: number,
  minHeight: number = laneHeight
): number => Math.max(laneHeight * laneCount, minHeight);

export const ROW_LABEL_STYLES = {
  container:
    "aero:w-48 aero:flex-shrink-0 aero:p-3 aero:border-r aero:border-border/50 aero:space-y-1 aero:bg-background/50",
  title:
    "aero:font-semibold aero:text-sm aero:text-brand-primary-600 aero:truncate",
  subtitle: "aero:text-xs aero:text-muted-foreground aero:truncate",
  detail: "aero:text-xs aero:font-medium aero:truncate",
  badgeContainer:
    "aero:flex aero:items-center aero:gap-1.5 aero:pt-1.5 aero:flex-wrap"
} as const;

export const SCHEDULE_ROW_STYLES = {
  row: "aero:flex aero:border-b aero:border-border/30 aero:last:border-b-0 aero:hover:bg-muted/30 aero:transition-colors",
  gridContainer: "aero:flex-1 aero:relative"
} as const;

export const BAR_TEXT_STYLES = {
  title: "aero:font-semibold aero:truncate aero:leading-tight",
  subtitle: "aero:truncate aero:opacity-90 aero:leading-tight",
  detail: "aero:truncate aero:opacity-80 aero:leading-tight",
  icon: "aero:inline aero:mr-1 aero:flex-shrink-0",
  iconSmall: "aero:h-2.5 aero:w-2.5",
  iconMedium: "aero:h-3 aero:w-3"
} as const;

export const TOOLTIP_STYLES = {
  container: "aero:space-y-1.5 aero:p-1",
  title: "aero:font-medium aero:text-sm",
  subtitle: "aero:text-xs aero:text-muted-foreground",
  detail: "aero:text-xs",
  row: "aero:flex aero:items-center aero:gap-1.5 aero:text-xs"
} as const;

// ── OverflowIndicatorCard ─────────────────────────────────────────────────────
// Shown in the weekly 100%-zoom detail view when >2 bars overlap in the same
// time slot.
//
// Bar: w=40, left side shows stacked color accent strips (one per hidden item),
//      "+N" centered. Dashed green border like all other bars.
// Hover: stacks one full ScheduleHoverCard per hidden item (Figma 731-125883).

export interface OverflowItem extends ScheduleHoverCardData {}

interface OverflowIndicatorCardProps {
  /** Number of hidden (overflow) items = label shown as "+N". */
  count: number;
  /** Absolute top position in px inside the day column. */
  top: number;
  /** Absolute left position in px inside the day column. */
  left: number;
  /** Height in px — spans the overflow items' collective time range. */
  height: number;
  /** Full data for hidden items — one card per item shown in the tooltip. */
  items: OverflowItem[];
  /** Tooltip variant forwarded to ScheduleHoverCard. */
  variant?: "technician" | "machine" | "job";
}

/** Resolves the solid accent color from the same priority-color string used by ScheduleHoverCard. */
function accentFromPriority(priorityColor: string): string {
  if (priorityColor.includes("green")) return "#16a34a"; // green-600
  if (priorityColor.includes("yellow")) return "#ca8a04"; // yellow-600
  if (priorityColor.includes("red")) return "#dc2626"; // red-600
  return "#2563eb"; // blue-600
}

export function OverflowIndicatorCard({
  count,
  top,
  left,
  height,
  items,
  variant = "technician"
}: OverflowIndicatorCardProps) {
  const barH = Math.max(height, 24);
  const segH = barH / items.length;

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <div
            className={cn(
              getAssignmentBarClasses(
                JOB_BAR_COLOR,
                false,
                false,
                false,
                "aero:text-neutral-900",
                "dashed-full"
              ),
              "aero:flex aero:items-center aero:justify-center"
            )}
            style={{
              position: "absolute",
              top,
              left,
              width: 40,
              height: barH,
              zIndex: 4,
              padding: 0
            }}
          >
            {/* Stacked color accent strips — one per hidden item */}
            <div className="aero:absolute aero:left-0 aero:top-0 aero:bottom-0 aero:w-[3px] aero:rounded-l aero:overflow-hidden">
              {items.map((item, i) => (
                <div
                  key={i}
                  style={{
                    height: segH,
                    backgroundColor: accentFromPriority(item.priorityColor)
                  }}
                />
              ))}
            </div>
            <span className="aero:text-[11px] aero:font-semibold aero:text-neutral-900 aero:select-none aero:z-10">
              +{count}
            </span>
          </div>
        </TooltipTrigger>
        <TooltipContent
          side="right"
          className="aero:p-0 aero:border-0 aero:bg-transparent aero:shadow-none"
        >
          {/* Outer container provides the single rounded frame + shadow.
              overflow-hidden clips children to the border-radius so no per-card
              rounding is needed. Divider between cards comes from border-b on
              every card except the last. */}
          <div className="aero:rounded-md aero:shadow-lg aero:border aero:border-border aero:overflow-hidden aero:max-h-[80vh] aero:overflow-y-auto">
            {items.map((item, i) => (
              <ScheduleHoverCard
                key={i}
                variant={variant}
                data={item}
                className={cn(
                  "aero:rounded-none aero:shadow-none aero:border-0",
                  i < items.length - 1 && "aero:border-b aero:border-border"
                )}
              />
            ))}
          </div>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
